import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import logoMinimal from "@/assets/vikra-logo-transparent.png";


const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const scrollToSection = (sectionId: string) => {
    const scrollToElement = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
      }
    };

    if (location.pathname !== "/") {
      navigate("/");
      // Wait for navigation to complete before scrolling
      setTimeout(scrollToElement, 100);
    } else {
      scrollToElement();
    }
  };

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    
    if (href.startsWith("#")) {
      const sectionId = href.substring(1);
      scrollToSection(sectionId);
    } else {
      navigate(href);
    }
  };

  const navLinks = [
    { href: "/om-oss", label: "Om oss" },
    { href: "#gratis-utkast", label: "Gratis utkast" },
    { href: "#tjenester", label: "Tjenester" },
    { href: "#kundecase", label: "Kundecase" },
    { href: "/prosess", label: "Prosess" },
    { href: "/faq", label: "FAQ" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/90 backdrop-blur-xl border-b border-white/[0.06] shadow-lg py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container-width">
        <div className="flex items-center justify-between gap-4">
          <button onClick={handleLogoClick} className="flex items-center cursor-pointer shrink-0">
            <img src={logoMinimal} alt="Vikra Group logo" width={320} height={180} fetchPriority="high" decoding="async" className="h-16 sm:h-20 lg:h-16 xl:h-20 2xl:h-24 w-auto object-contain" />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6 2xl:gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="whitespace-nowrap text-[13px] xl:text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 bg-transparent border-none cursor-pointer"
              >
                {link.label}
              </button>
            ))}
            <Button 
              variant="cta" 
              size="sm"
              className="whitespace-nowrap shrink-0"
              onClick={() => scrollToSection('kontakt')}
            >
              Start prosjektet
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-white/[0.06] shadow-lg animate-fade-up">
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="block w-full text-left text-base font-medium text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
              <Button 
                variant="cta" 
                className="w-full mt-4"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  scrollToSection('kontakt');
                }}
              >
                Start prosjektet
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
